import { createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
// import { message } from 'antd';
import moment from 'moment';
import { API } from 'api';
import { getUIVersion } from 'utils/router-static-data';

const initialState = {};

export const commonSlice = createSlice({
	name: 'common', // name of the reducer
	initialState,
	reducers: {},
});

export const updateStatus = (data) => async () => {
	const CREDENTIALS = {
		url: '/update',
		data,
	};

	return API.common(CREDENTIALS).then(async (response) => response);
};

export const updateResetPassword = (data) => async () => {
	const CREDENTIALS = {
		url: '/reset-password',
		data,
	};

	return API.common(CREDENTIALS).then(async (response) => response);
};

export const getSignedUrl = (data) => async () => {
	const CREDENTIALS = {
		url: '/get-signed-url',
		data,
	};

	return API.common(CREDENTIALS).then(async (response) => response);
};

export const uploadFileToS3AndGetKey =
	({ entity, file }) =>
	async (dispatch) => {
		const signed = await dispatch(
			getSignedUrl({ url_type: 'upload', data: { entity, keys: [file.name] } })
		);
		const target = signed?.data?.[0];
		if (!target?.url) throw new Error('Could not get an upload URL. Please try again.');

		const uploaded = await fetch(target.url, { method: 'PUT', body: file });
		if (!uploaded.ok) throw new Error('Could not upload the file. Please try again.');

		return target.filename || file.name;
	};

export const uploadFilesToS3 =
	({ url, data }, filetype) =>
	async () => {
		// console.log('url', url);
		// console.log('data', data);
		// console.log(filetype);

		const requestOptions = {
			method: 'PUT',
			headers: { 'Content-Type': filetype },
			body: data,
		};
		// Perform the upload
		fetch(url, requestOptions)
			.then((response) => {
				// eslint-disable-next-line sonarjs/no-all-duplicated-branches
				if (response.status === 200) {
					message.success('Files uploaded successfully');
				} else {
					message.error('Files upload Failed');
				}
			})
			.catch(() => {
				message.error('File Not uploaded');
			});
	};
export const uploadFilesToS3WithoutMessage =
	({ url, data }, filetype) =>
	async () => {
		const requestOptions = {
			method: 'PUT',
			headers: { 'Content-Type': filetype },
			body: data,
		};
		// Perform the upload
		fetch(url, requestOptions);
	};
export const getClientContractExpire =
	({ limit = 10, offset = 10, currentPage, ...rest }) =>
	async () => {
		// console.log('limit', limit);
		// console.log('offset', offset);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_contract',
				offset,
				limit,
				filter_by: {},
				...rest,
			},
		};

		return API.common(CREDENTIALS).then((response) => response);
	};
// Helper function to build v2 credentials for vendor contract
const buildVendorContractV2Credentials = (filterBy, offset, limit) => {
	const page = offset > 0 ? Math.floor(offset / limit) + 1 : 1;
	const perPage = limit;

	return {
		url: '/search',
		data: {
			search_type: 'vendor_contract',
			filter_by: filterBy,
			pagination: {
				page,
				per_page: perPage,
			},
		},
		version: 'v2',
	};
};

// Helper function to build v2 credentials for client contract
const buildClientContractV2Credentials = (filterBy, offset, limit) => ({
	url: '/search',
	data: {
		search_type: 'client_contract',
		offset: String(offset),
		limit: String(limit),
		fetch_filters: true,
		filter_list: {},
		filter_by: filterBy,
	},
	version: 'v2',
});

// Helper function to transform v2 response to v1 format
const transformV2ResponseToV1 = (response, searchType) => {
	const data = response?.data || {};
	let contracts = [];

	if (searchType === 'vendor_contract') {
		contracts = Array.isArray(data) ? data : data.contracts || [];
	} else if (searchType === 'client_contract') {
		contracts = data.contracts || [];
	}

	return {
		...response,
		data: contracts,
		meta: {
			...response.meta,
			count: response.meta?.count || data.pagination?.total || contracts.length || 0,
		},
	};
};

export const getContractExpire =
	({ search_type = '', limit = 10, offset = 0, currentPage, ...rest }) =>
	async () => {
		// console.log('limit', limit);
		// console.log('offset', offset);

		const dates = moment().add(15, 'days').calendar();

		// console.log(dates);

		// Use v2 API for vendor_contract and client_contract when in new UI
		const isV2UI = getUIVersion() === 'new';
		const v2ContractTypes = ['vendor_contract', 'client_contract'];
		const useV2 = isV2UI && v2ContractTypes.includes(search_type);

		// Build filter_by object
		const filterBy = {
			expiry_date: dates,
			expiry_count_search: true,
			is_active: true,
			...rest,
		};

		// Build credentials based on API version
		let CREDENTIALS;
		if (useV2 && search_type === 'vendor_contract') {
			CREDENTIALS = buildVendorContractV2Credentials(filterBy, offset, limit);
		} else if (useV2 && search_type === 'client_contract') {
			CREDENTIALS = buildClientContractV2Credentials(filterBy, offset, limit);
		} else {
			// V1 API structure (backward compatibility)
			CREDENTIALS = {
				url: '/search',
				data: {
					search_type,
					offset,
					limit,
					filter_by: filterBy,
				},
			};
		}

		return API.common(CREDENTIALS).then((response) => {
			// Transform v2 response to v1 format for backward compatibility
			if (useV2) {
				return transformV2ResponseToV1(response, search_type);
			}

			// Return v1 response as-is
			return response;
		});
	};

export const getSearchData =
	({ search_type = '', limit = 10, offset = 0, currentPage, ...rest }) =>
	async () => {
		const dates = moment().add(15, 'days').calendar();

		// console.log(dates);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				offset,
				limit,
				filter_list: {},
				filter_by: {
					expiry_date: dates,
					expiry_count_search: true,
					is_active: true,
				},
				...rest,
			},
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

export const airBulkUploadApi =
	({ data_key, setErrors }) =>
	async () => {
		// console.log('setErrors', data_key);
		const url = '/bulk-upload';

		const apiData = {
			upload_type: 'awb',
			data: {
				key: data_key,
			},
		};

		const CREDENTIALS = {
			url,
			data: apiData,
			setErrors,
			aclBlockApiCall: false,
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data,
		}));
	};
export const podUpload =
	({ data_key, id, trip_id, setErrors, charges, pod_force_upload }) =>
	async () => {
		const url = '/update';
		let normalizedKey;
		if (getUIVersion() === 'new') {
			// V2: key should be an array
			if (Array.isArray(data_key)) {
				normalizedKey = data_key.filter(Boolean);
			} else if (data_key) {
				normalizedKey = [data_key];
			} else {
				normalizedKey = [];
			}
		} else if (Array.isArray(data_key)) {
			// V1: key should be a string
			normalizedKey = data_key.filter(Boolean)[0] || '';
		} else {
			normalizedKey = data_key || '';
		}

		const apiData = {
			update_type: 'upload_proof_of_delivery',
			data: {
				consignment_note_id: id,
				// eslint-disable-next-line object-shorthand
				trip_id: trip_id,
				key: normalizedKey,
				additional_charges: charges,
			},
			...(getUIVersion() === 'new' && { version: 'v2' }),
		};

		if (pod_force_upload === true) {
			apiData.data.pod_force_upload = true;
		}
		console.log(apiData, 'apiData');
		const CREDENTIALS = {
			url,
			data: apiData,
			setErrors,
			aclBlockApiCall: false,
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data,
		}));
	};
export const newPodUpload =
	({ data_key, id, lrId, setErrors, charges, pod_force_upload, additional_charges_proof_key }) =>
	async () => {
		const url = '/update';
		const isV2UI = getUIVersion() === 'new';
		let normalizedKey;
		if (isV2UI) {
			// V2: key should be an array
			if (Array.isArray(data_key)) {
				normalizedKey = data_key.filter(Boolean);
			} else if (data_key) {
				normalizedKey = [data_key];
			} else {
				normalizedKey = [];
			}
		} else if (Array.isArray(data_key)) {
			// V1: key should be a string
			normalizedKey = data_key.filter(Boolean)[0] || '';
		} else {
			normalizedKey = data_key || '';
		}

		const apiData = {
			update_type: 'upload_proof_of_delivery',
			data: {
				trip_id: id,
				consignment_note_id: lrId,
				key: normalizedKey,
				additional_charges: charges,
				additional_charges_proof_key,
			},
		};

		if (pod_force_upload === true) {
			apiData.data.pod_force_upload = true;
		}

		const CREDENTIALS = {
			url,
			data: apiData,
			setErrors,
			aclBlockApiCall: false,
			...(isV2UI && { version: 'v2' }),
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data,
		}));
	};

export const commonSearch =
	({ search_type = '', limit = 10, offset = 0, currentPage, version, ...rest }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				limit,
				offset,
				filter_by: { ...rest },
			},
		};
		if (version) CREDENTIALS.version = version;

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data,
			count: response?.meta?.count,
		}));
	};

export const commonCreate =
	({ create_type, formdata, setErrors }) =>
	async () => {
		const data = {
			create_type,
			data: formdata,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
		};

		console.log(CREDENTIALS);

		return API.common(CREDENTIALS).then((res) => res);
		// .catch((res) => res);
	};

export const commonUpdate =
	({ update_type = '', ...rest }) =>
	async () => {
		const data = {
			update_type,
			...rest,
		};
		const CREDENTIALS = {
			url: '/update',
			data,
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

export const getClientGstins =
	({ client_id, limit = 100, offset = 0 }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_gstins',
				filter_by: {
					client_id,
				},
				limit,
				offset,
			},
		};

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data || [],
			count: response?.meta?.count || 0,
		}));
	};

export default commonSlice.reducer;

/**
 * Fetch contracts similar to the one being created/edited (Similar Contracts modal).
 * Matches on vehicle type (+ billing type / sub billing type when set), scoped either to
 * the same project (scope 'project') or to the same city as the project (scope 'city').
 */
export const getSimilarContractsV2 =
	({
		contractType,
		scope,
		project_id,
		vehicle_type_id,
		billing_type,
		billing_subtype,
		exclude_id,
	}) =>
	async () => {
		const filterBy = { vehicle_type_id, is_active: true };
		if (billing_type) filterBy.billing_type = billing_type;
		if (billing_subtype) filterBy.billing_subtype = billing_subtype;
		if (exclude_id) filterBy.exclude_id = exclude_id;
		if (scope === 'city') filterBy.city_from_project_id = project_id;
		else filterBy.project_id = project_id;

		const CREDENTIALS =
			contractType === 'vendor'
				? buildVendorContractV2Credentials(filterBy, 0, 100)
				: buildClientContractV2Credentials(filterBy, 0, 100);

		return API.common(CREDENTIALS).then((response) => response?.data?.contracts || []);
	};
